import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getQuote, getQuotePdfUrl } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { ArrowLeft, PencilSimple, Printer, WarningCircle, CheckCircle, Truck, Image as ImageIcon, FilePdf } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const MIN_ORDER_VALUE = 5000;
const SHIPPING_SURCHARGE = 400;

const statusLabels = {
    draft: 'Draft', internal_review: 'Internal Review', sent: 'Sent to Customer',
    revised: 'Revised', won: 'Won', lost: 'Lost',
};
const statusColors = {
    draft: 'bg-slate-100 text-slate-700', internal_review: 'bg-blue-100 text-blue-700',
    sent: 'bg-indigo-100 text-indigo-700', revised: 'bg-amber-100 text-amber-700',
    won: 'bg-emerald-100 text-emerald-700', lost: 'bg-red-100 text-red-700',
};

function PreviewImage({ src, alt }) {
    if (!src) {
        return (
            <div className="w-16 h-16 rounded-md bg-muted/60 border border-border/40 flex items-center justify-center flex-shrink-0 print:w-12 print:h-12">
                <ImageIcon size={20} className="text-muted-foreground/50" />
            </div>
        );
    }
    return (
        <img
            src={src}
            alt={alt || 'Product'}
            className="w-16 h-16 rounded-md object-cover flex-shrink-0 bg-muted border border-border/40 print:w-12 print:h-12"
            onError={(e) => { e.target.style.display = 'none'; }}
        />
    );
}

function dimWithUnit(d) { if (!d) return ''; return d.toString().toLowerCase().includes('mm') ? d : `${d} mm`; }

export default function QuotePreview() {
    const { quoteId } = useParams();
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await getQuote(quoteId);
                setQuote(res.data);
            } catch { toast.error('Failed to load quote'); }
            finally { setLoading(false); }
        })();
    }, [quoteId]);

    if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-96" /></div>;
    if (!quote) return null;

    const lines = quote.lines || [];
    const subtotal = lines.reduce((sum, l) => sum + (l.line_total || 0), 0);
    const isEUR = quote.currency === 'EUR';
    const surchargeApplied = quote.surcharge_applied || false;
    const belowThreshold = isEUR && subtotal > 0 && subtotal < MIN_ORDER_VALUE;
    const surchargeAmount = surchargeApplied ? SHIPPING_SURCHARGE : 0;
    const finalTotal = subtotal + surchargeAmount;

    return (
        <div className="max-w-4xl mx-auto space-y-6" data-testid="quote-preview">
            <div className="flex items-center justify-between print:hidden">
                <Button variant="ghost" size="sm" asChild><Link to={`/quotes/${quoteId}`}><ArrowLeft size={16} className="mr-1" />Back to Editor</Link></Button>
                <div className="flex gap-2">
                    <Button variant="outline" asChild><Link to={`/quotes/${quoteId}`}><PencilSimple size={16} className="mr-2" />Edit</Link></Button>
                    <Button variant="outline" onClick={() => { const url = getQuotePdfUrl(quoteId); window.open(url, '_blank'); }} data-testid="preview-export-pdf">
                        <FilePdf size={16} className="mr-2" />Export PDF
                    </Button>
                    <Button variant="outline" onClick={() => window.print()}><Printer size={16} className="mr-2" />Print</Button>
                </div>
            </div>

            {/* Print-friendly quote document */}
            <div className="bg-card border border-border rounded-lg print:border-0 print:shadow-none" id="quote-document">
                {/* Header */}
                <div className="p-8 border-b border-border">
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-2xl font-bold tracking-tight">QUOTATION</h1>
                            <p className="text-lg font-mono font-semibold mt-1" data-testid="preview-quote-number">{quote.quote_number}</p>
                            <div className="flex items-center gap-2 mt-1">
                                <span className="text-sm text-muted-foreground">Version {quote.version}</span>
                                <Badge className={`text-[11px] ${statusColors[quote.status] || ''}`}>
                                    {statusLabels[quote.status] || quote.status}
                                </Badge>
                            </div>
                        </div>
                        <div className="text-right text-sm">
                            <p className="font-semibold text-base">Lamikraft</p>
                            <p className="text-muted-foreground mt-1">Date: {formatDate(quote.created_at)}</p>
                            <p className="text-muted-foreground">Valid for: {quote.validity_days} days</p>
                        </div>
                    </div>
                </div>

                {/* Customer Info */}
                <div className="p-8 border-b border-border grid grid-cols-2 gap-6">
                    <div>
                        <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium mb-1">Customer</p>
                        <p className="font-semibold text-base">{quote.customer_name || '—'}</p>
                        {quote.customer_country && <p className="text-sm text-muted-foreground">{quote.customer_country}</p>}
                        {quote.customer_type && quote.customer_type !== 'standard' && (
                            <p className="text-sm text-muted-foreground capitalize mt-0.5">Type: {quote.customer_type}</p>
                        )}
                        {quote.customer_reference && <p className="text-sm text-muted-foreground mt-1">Ref: {quote.customer_reference}</p>}
                    </div>
                    <div className="text-sm space-y-1">
                        <div><span className="text-muted-foreground">Currency:</span> <span className="font-medium">{quote.currency}</span></div>
                        <div><span className="text-muted-foreground">Incoterm:</span> <span className="font-medium">{quote.incoterm}</span></div>
                        {quote.payment_terms && <div><span className="text-muted-foreground">Payment:</span> <span className="font-medium">{quote.payment_terms}</span></div>}
                    </div>
                </div>

                {/* Line Items */}
                <div className="p-8">
                    <table className="w-full text-sm" data-testid="preview-line-table">
                        <thead>
                            <tr className="border-b-2 border-foreground/20">
                                <th className="text-left py-2.5 font-semibold w-8">#</th>
                                <th className="text-left py-2.5 font-semibold w-14"></th>
                                <th className="text-left py-2.5 font-semibold">Product</th>
                                <th className="text-right py-2.5 font-semibold">Rec. MOQ</th>
                                <th className="text-right py-2.5 font-semibold">Price</th>
                                <th className="text-right py-2.5 font-semibold">Qty</th>
                                <th className="text-right py-2.5 font-semibold">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lines.map((line, i) => (
                                <tr key={line.id} className="border-b border-border/50" data-testid={`preview-line-${line.id}`}>
                                    <td className="py-3 text-muted-foreground align-top">{i + 1}</td>
                                    <td className="py-3 align-top">
                                        {line.line_type !== 'manual' && <PreviewImage src={line.image_url} alt={line.product_name} />}
                                    </td>
                                    <td className="py-3 align-top">
                                        <div className="flex items-center gap-1.5 mb-0.5">
                                            <span className="font-mono text-xs font-semibold">{line.display_sku || line.display_code}</span>
                                            <Badge variant="outline" className="text-[9px] h-4 capitalize print:border-foreground/20">{line.line_type}</Badge>
                                        </div>
                                        <div className="text-sm font-medium">{line.product_name || line.description}</div>
                                        {line.line_type !== 'manual' && (
                                            <div className="text-xs text-muted-foreground mt-0.5 space-y-0">
                                                {line.dimensions && <span>{dimWithUnit(line.dimensions)}</span>}
                                                {line.thickness && <span> | {line.thickness}</span>}
                                                {line.decor_no && <span> | Decor: {line.decor_no}{line.decor_name ? ` — ${line.decor_name}` : ''}</span>}
                                                {line.finish && <span> | {line.finish}</span>}
                                            </div>
                                        )}
                                        {line.notes && <div className="text-[11px] text-muted-foreground italic mt-0.5">{line.notes}</div>}
                                    </td>
                                    <td className="py-3 text-right font-mono align-top">{line.moq?.toLocaleString() || '—'}</td>
                                    <td className="py-3 text-right font-mono align-top">
                                        {formatCurrency(line.quoted_price, line.currency)}
                                        {line.quoted_price !== line.default_price && line.default_price > 0 && (
                                            <div className="text-[10px] text-muted-foreground line-through">{formatCurrency(line.default_price, line.currency)}</div>
                                        )}
                                    </td>
                                    <td className="py-3 text-right font-mono align-top">{line.quantity?.toLocaleString() || '—'}</td>
                                    <td className="py-3 text-right font-mono font-semibold align-top">{formatCurrency(line.line_total, line.currency)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            {/* Subtotal */}
                            <tr className="border-t-2 border-foreground/20">
                                <td colSpan={6} className="py-2.5 text-right text-muted-foreground">Subtotal ({quote.currency})</td>
                                <td className="py-2.5 text-right font-mono font-semibold" data-testid="preview-subtotal">{formatCurrency(subtotal, quote.currency)}</td>
                            </tr>
                            {/* Shipping surcharge */}
                            {surchargeApplied && belowThreshold && (
                                <tr>
                                    <td colSpan={6} className="py-2 text-right text-amber-600 dark:text-amber-400 flex items-center justify-end gap-1.5 print:text-amber-700">
                                        <Truck size={14} /> Shipping surcharge (below EUR 5,000 min)
                                    </td>
                                    <td className="py-2 text-right font-mono font-semibold text-amber-600 dark:text-amber-400 print:text-amber-700" data-testid="preview-surcharge">
                                        + {formatCurrency(SHIPPING_SURCHARGE, 'EUR')}
                                    </td>
                                </tr>
                            )}
                            {/* Final total */}
                            <tr className={surchargeApplied && belowThreshold ? 'border-t border-border' : ''}>
                                <td colSpan={6} className="py-3 text-right font-bold text-base">Total ({quote.currency})</td>
                                <td className="py-3 text-right font-mono font-bold text-lg" data-testid="preview-total">{formatCurrency(finalTotal, quote.currency)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Commercial Notes */}
                <div className="px-8 pb-6 space-y-3 text-sm">
                    {/* Threshold indicator */}
                    {isEUR && subtotal > 0 && (
                        <div className={`flex items-center gap-2 p-3 rounded-md ${belowThreshold && surchargeApplied ? 'bg-amber-50 border border-amber-200 text-amber-700 dark:bg-amber-950/20 dark:border-amber-800 dark:text-amber-400' : belowThreshold ? 'bg-amber-50/50 border border-amber-100 text-amber-600 dark:bg-amber-950/10 dark:border-amber-800 dark:text-amber-400' : 'bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-950/20 dark:border-emerald-800 dark:text-emerald-400'}`} data-testid="preview-threshold-indicator">
                            {!belowThreshold ? (
                                <>
                                    <CheckCircle size={16} weight="fill" />
                                    <span>Meets minimum order value (EUR 5,000).</span>
                                </>
                            ) : surchargeApplied ? (
                                <>
                                    <WarningCircle size={16} weight="fill" />
                                    <span>Below minimum order value. EUR 400 shipping surcharge applied.</span>
                                </>
                            ) : (
                                <>
                                    <WarningCircle size={16} weight="fill" />
                                    <span>Below minimum order value (EUR 5,000). Shipping surcharge not applied.</span>
                                </>
                            )}
                        </div>
                    )}
                    {!isEUR && subtotal > 0 && (
                        <div className="p-3 bg-muted/50 rounded-md text-muted-foreground text-xs">
                            Minimum order value: EUR 5,000 equivalent. Orders below this threshold are subject to a EUR 400 shipping surcharge.
                        </div>
                    )}

                    {quote.min_invoice_note && (
                        <div className="p-3 bg-muted/30 rounded text-muted-foreground text-xs">
                            {quote.min_invoice_note}
                        </div>
                    )}
                    {quote.remarks && (
                        <div>
                            <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium mb-1">Remarks</p>
                            <p className="text-sm">{quote.remarks}</p>
                        </div>
                    )}
                </div>

                {/* Revision History */}
                {quote.revisions && quote.revisions.length > 0 && (
                    <div className="px-8 pb-8 print:hidden">
                        <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium mb-2">Revision History</p>
                        <div className="space-y-2">
                            {quote.revisions.map(r => (
                                <div key={r.id} className="text-xs flex items-center gap-3 text-muted-foreground">
                                    <Badge variant="outline" className="text-[10px]">V{r.version}</Badge>
                                    <span>{r.status_from} → {r.status_to}</span>
                                    <span>{r.changes_summary}</span>
                                    <span className="ml-auto">{formatDate(r.created_at)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
